import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type PillTone = "ok" | "gold" | "navy" | "warn" | "danger" | "muted";

interface PillProps {
  tone?: PillTone;
  live?: boolean;
  children: ReactNode;
  className?: string;
}

const TONE: Record<PillTone, string> = {
  ok: "bg-[rgba(39,161,85,0.1)] text-[#27a155] border-[rgba(39,161,85,0.25)]",
  gold: "bg-[rgba(255,200,61,0.15)] text-[#b57a00] border-[rgba(255,200,61,0.3)]",
  navy: "bg-[rgba(8,37,76,0.08)] text-[#08254c] border-[rgba(8,37,76,0.15)]",
  warn: "bg-[rgba(242,115,37,0.12)] text-[#c25500] border-[rgba(242,115,37,0.3)]",
  danger: "bg-[rgba(212,53,60,0.1)] text-[#b02a31] border-[rgba(212,53,60,0.25)]",
  muted: "bg-[#f2f4f7] text-[#7b8799] border-[#e8ebf0]",
};

const DOT: Record<PillTone, { bg: string; shadow?: string }> = {
  ok: { bg: "#27a155", shadow: "0 0 6px #27a155" },
  gold: { bg: "#ffc83d", shadow: "0 0 6px #ffc83d" },
  navy: { bg: "#08254c" },
  warn: { bg: "#f27325", shadow: "0 0 6px #f27325" },
  danger: { bg: "#d4353c", shadow: "0 0 6px #d4353c" },
  muted: { bg: "#b2bccb" },
};

export function Pill({ tone = "navy", live = false, children, className }: PillProps) {
  const dot = DOT[tone];
  const showDot = live || tone === "ok";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-[0.08em]",
        TONE[tone],
        className,
      )}
    >
      {showDot && (
        <span
          className={cn("w-1.5 h-1.5 rounded-full", live && "mg-pulse")}
          style={{ background: dot.bg, boxShadow: dot.shadow }}
        />
      )}
      {children}
    </span>
  );
}
